import type { FilterParams } from '@api/hooks/useBatch.ts';
import { FiltersModal } from '@components/FiltersModal/FiltersModal.tsx';
import { Checkbox, ControlledTextInput, SelectInput } from '@gurupay/gp-ui-library';
import { OPERATION_TYPE } from '@pages/batch-operations/utils.ts';
import type { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { VOP_STATUS } from 'types/layout.ts';
import { OperationStatus } from 'types/operations.ts';

const paymentStatusOptions = [
	{
		label: 'payments.batch.table.paymentStatus.unsigned',
		value: OperationStatus.CREATED,
	},
	{
		label: 'payments.batch.table.paymentStatus.signed',
		value: OperationStatus.SIGNED,
	},
	{
		label: 'payments.batch.table.paymentStatus.failed',
		value: OperationStatus.FAILED,
	},
	{
		label: 'payments.batch.table.paymentStatus.processed',
		value: OperationStatus.PROCESSED,
	},
	{
		label: 'payments.batch.table.paymentStatus.executed',
		value: OperationStatus.EXECUTED,
	},
];

interface BatchFilterProps {
	onFiltersSubmit: (filters: FilterParams) => void;
	hasFilters: boolean;
	handleClear: () => void;
}

export const BatchTransactionFilters: FC<BatchFilterProps> = ({
	onFiltersSubmit,
	hasFilters,
	handleClear,
}) => {
	const { t } = useTranslation();
	const { control, handleSubmit } = useFormContext<FilterParams>();

	const VOP_OPTIONS = [
		{
			label: t('filters.vopStatus.match'),
			value: VOP_STATUS.MATCH,
		},
		{
			label: t('filters.vopStatus.no_match'),
			value: VOP_STATUS.NO_MATCH,
		},
		{
			label: t('filters.vopStatus.close_match'),
			value: VOP_STATUS.CLOSE_MATCH,
		},
	];

	const PAYMENT_TYPES = [
		{
			label: t('filters.internal'),
			value: OPERATION_TYPE.SEPA,
		},
		{
			label: t('filters.international'),
			value: OPERATION_TYPE.INTERNATIONAL,
		},
	];

	return (
		<FiltersModal
			hasFilters={hasFilters}
			onClear={handleClear}
			onSubmit={handleSubmit(onFiltersSubmit)}
		>
			<Controller
				control={control}
				name="type"
				render={({ field }) => (
					<SelectInput
						{...field}
						className="max-w-[500px]"
						label={t('filters.type')}
						options={PAYMENT_TYPES}
						showClear
					/>
				)}
			/>
			<ControlledTextInput label={t('filters.beneficiaryIban')} name="beneficiaryIban" />
			<ControlledTextInput label={t('filters.beneficiaryName')} name="beneficiaryName" />
			<ControlledTextInput label={t('filters.paymentDescription')} name="description" />

			<Controller
				control={control}
				name="verification_of_payee_status"
				render={({ field }) => (
					<SelectInput
						{...field}
						className="max-w-[500px]"
						label={t('filters.vop')}
						options={VOP_OPTIONS}
						showClear
					/>
				)}
			/>

			<Controller
				control={control}
				name="status"
				render={({ field }) => {
					const { onChange, value = [] } = field;

					return (
						<div className="flex flex-wrap gap-space-16">
							{paymentStatusOptions.map((item) => {
								const handleChange = (itemValue: boolean) => {
									if (itemValue) {
										onChange([...value, item.value]);
									} else {
										onChange(value.filter((v) => v !== item.value));
									}
								};

								return (
									<Checkbox
										key={item.label}
										checked={value.includes(item.value as OperationStatus)}
										label={t(item.label)}
										onChange={handleChange}
									/>
								);
							})}
						</div>
					);
				}}
			/>
		</FiltersModal>
	);
};

BatchTransactionFilters.displayName = 'BatchTransactionFilter';
